import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { FuelTransaction, FuelTransactionCreate } from '@/types/fuel'
import type { Equipment } from '@/types/equipment'
import { fuelApi } from '@/api/fuelApi'
import { useEquipmentStore } from '@/stores/equipmentStore'

export const useFuelStore = defineStore('fuel', () => {
  const transactions = ref<FuelTransaction[]>([])
  const currentTransaction = ref<FuelTransaction | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  // Per-equipment aggregated stats computed from transactions
  const equipmentStats = ref<
    Array<{ equipment_id: number; total_fuel_used: number; average_cost_per_liter: number }>
  >([])

  const fetchTransactions = async () => {
    loading.value = true
    error.value = null
    try {
      const txs = await fuelApi.getAll()
      // Ensure equipment store has data so we can enrich transactions with equipment objects
      const equipmentStore = useEquipmentStore()
      if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
        // attempt to fetch equipment list (best-effort)
        try {
          await equipmentStore.fetchEquipment()
        } catch {
          // ignore - enrichment will fallback to ids
        }
      }

      // Enrich transactions with equipment objects when available
      transactions.value = Array.isArray(txs)
        ? txs.map((t) => {
            const obj = t as unknown
            const objRec = obj as Record<string, unknown>
            const equipmentFromApiRaw =
              'equipment' in objRec ? (objRec['equipment'] as unknown) : undefined
            const equipmentFromApi = equipmentFromApiRaw as unknown as Equipment | undefined
            const equipmentIdFromTx =
              'equipment_id' in objRec ? Number(objRec['equipment_id'] as unknown) : undefined
            const enriched = {
              ...(t as FuelTransaction),
              equipment:
                equipmentFromApi ??
                (Array.isArray(equipmentStore.equipment) && equipmentIdFromTx
                  ? equipmentStore.equipment.find((e) => e.id === Number(equipmentIdFromTx))
                  : undefined),
            } as unknown as FuelTransaction
            return enriched
          })
        : []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch fuel transactions'
    } finally {
      loading.value = false
      // recompute equipment stats whenever transactions change
      computeEquipmentStats()
    }
  }

  const computeEquipmentStats = () => {
    const map = new Map<number, { equipment_id: number; totalLiters: number; totalCost: number }>()
    const tx = Array.isArray(transactions.value) ? transactions.value : []
    for (const t of tx) {
      const eq = Number(t?.equipment_id) || 0
      const liters = typeof t?.liters === 'number' ? t.liters : Number(t?.liters) || 0
      const tc = typeof t?.total_cost === 'number' ? t.total_cost : undefined
      const cpl =
        typeof t?.cost_per_liter === 'number' ? t.cost_per_liter : Number(t?.cost_per_liter) || 0
      const cost = typeof tc === 'number' && Number.isFinite(tc) ? tc : liters * cpl

      const existing = map.get(eq)
      if (existing) {
        existing.totalLiters += liters
        existing.totalCost += cost
      } else {
        map.set(eq, { equipment_id: eq, totalLiters: liters, totalCost: cost })
      }
    }

    equipmentStats.value = Array.from(map.values()).map((v) => ({
      equipment_id: v.equipment_id,
      total_fuel_used: v.totalLiters,
      average_cost_per_liter: v.totalLiters > 0 ? v.totalCost / v.totalLiters : 0,
    }))
  }

  const fetchEquipmentStats = async () => {
    // Ensure we have transactions, then compute stats
    if (!Array.isArray(transactions.value) || transactions.value.length === 0) {
      await fetchTransactions()
    } else {
      computeEquipmentStats()
    }
  }

  // Derived stats used in UI - keep as numbers to avoid runtime errors when calling .toFixed
  const totalFuelUsed = computed(() => {
    const tx = Array.isArray(transactions.value) ? transactions.value : []
    return tx.reduce((sum, t) => {
      const liters = typeof t?.liters === 'number' ? t.liters : Number(t?.liters) || 0
      return sum + liters
    }, 0)
  })

  const totalFuelCost = computed(() => {
    const tx = Array.isArray(transactions.value) ? transactions.value : []
    return tx.reduce((sum, t) => {
      // prefer total_cost if provided, else compute from liters * cost_per_liter
      const tc = typeof t?.total_cost === 'number' ? t.total_cost : undefined
      if (typeof tc === 'number' && Number.isFinite(tc)) return sum + tc
      const liters = typeof t?.liters === 'number' ? t.liters : Number(t?.liters) || 0
      const cpl =
        typeof t?.cost_per_liter === 'number' ? t.cost_per_liter : Number(t?.cost_per_liter) || 0
      return sum + liters * cpl
    }, 0)
  })

  const averageCostPerLiter = computed(() => {
    const liters = totalFuelUsed.value
    return liters > 0 ? totalFuelCost.value / liters : 0
  })

  const fetchTransactionById = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      currentTransaction.value = await fuelApi.getById(id)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch fuel transaction'
      throw err
    } finally {
      loading.value = false
    }
  }

  const createTransaction = async (data: FuelTransactionCreate) => {
    loading.value = true
    error.value = null
    try {
      const newTransaction = await fuelApi.create(data)

      // enrich created transaction with equipment if available
      const equipmentStore = useEquipmentStore()
      if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
        try {
          await equipmentStore.fetchEquipment()
        } catch {
          // ignore
        }
      }

      const nj = newTransaction as unknown
      const njRec = nj as Record<string, unknown>

      let newEqId: number | undefined
      const rawNewEq = njRec['equipment']
      if (typeof rawNewEq === 'number') {
        newEqId = rawNewEq
      } else if (rawNewEq && typeof rawNewEq === 'object' && 'id' in rawNewEq) {
        newEqId = Number((rawNewEq as Record<string, unknown>)['id'])
      }
      if (!newEqId && 'equipment_id' in njRec) {
        newEqId = Number(njRec['equipment_id'])
      }

      const foundNewEq =
        newEqId && Array.isArray(equipmentStore.equipment)
          ? equipmentStore.equipment.find((e) => e.id === newEqId)
          : undefined
      const finalNewEq = foundNewEq || (typeof rawNewEq === 'object' ? rawNewEq as Equipment : undefined)

      const enriched = {
        ...(newTransaction as FuelTransaction),
        equipment_id: newEqId,
        equipment: finalNewEq,
      } as unknown as FuelTransaction

      // Debug: log response shape and current transactions size
      try {
        console.debug('[fuelStore] createTransaction: received', newTransaction)
      } catch { }

      transactions.value.push(enriched)
      // Debug: log after push
      try {
        console.debug('[fuelStore] transactions count after create:', transactions.value.length)
      } catch { }

      computeEquipmentStats()
      return enriched
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create fuel transaction'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateTransaction = async (id: number, data: Partial<FuelTransactionCreate>) => {
    loading.value = true
    error.value = null
    try {
      const updatedTransaction = await fuelApi.update(id, data)

      // enrich updated transaction
      const equipmentStore = useEquipmentStore()
      if (!Array.isArray(equipmentStore.equipment) || equipmentStore.equipment.length === 0) {
        try {
          await equipmentStore.fetchEquipment()
        } catch {
          // ignore
        }
      }

      const uj = updatedTransaction as unknown
      const ujRec = uj as Record<string, unknown>

      let updatedEqId: number | undefined
      const rawUpEq = ujRec['equipment']
      if (typeof rawUpEq === 'number') {
        updatedEqId = rawUpEq
      } else if (rawUpEq && typeof rawUpEq === 'object' && 'id' in rawUpEq) {
        updatedEqId = Number((rawUpEq as Record<string, unknown>)['id'])
      }
      if (!updatedEqId && 'equipment_id' in ujRec) {
        updatedEqId = Number(ujRec['equipment_id'])
      }

      const foundUpEq =
        updatedEqId && Array.isArray(equipmentStore.equipment)
          ? equipmentStore.equipment.find((e) => e.id === updatedEqId)
          : undefined
      const finalUpEq = foundUpEq || (typeof rawUpEq === 'object' ? rawUpEq as Equipment : undefined)

      const enriched = {
        ...(updatedTransaction as FuelTransaction),
        equipment_id: updatedEqId,
        equipment: finalUpEq,
      } as unknown as FuelTransaction

      const index = transactions.value.findIndex((t) => t.id === id)
      if (index !== -1) {
        transactions.value[index] = enriched
      }
      // Debug: log after update
      try {
        console.debug('[fuelStore] transactions count after update:', transactions.value.length)
      } catch {}

      computeEquipmentStats()
      return enriched
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update fuel transaction'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteTransaction = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      await fuelApi.delete(id)
      transactions.value = transactions.value.filter((t) => t.id !== id)
      computeEquipmentStats()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete fuel transaction'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    transactions,
    currentTransaction,
    loading,
    error,
    fetchTransactions,
    fetchTransactionById,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    totalFuelUsed,
    totalFuelCost,
    averageCostPerLiter,
    equipmentStats,
    fetchEquipmentStats,
  }
})
